import { useState } from "react";
import { t } from "@/i18n-js/instance";
import { useFetchPlan } from "@/react/components/shared/UpgradePlan/hooks/useFetchPlan";
import { planTiers } from "@/react/helpers/planHelpers";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import { useCreatePlanTrial } from "@circle-react/hooks/planTrials/useCreatePlanTrial";
import { Modal } from "@circle-react-uikit/ModalV2";
import { BlockedDowngradeScheduledState } from "./PlanTrialOptInModal/BlockedDowngradeScheduledState";
import { ErrorState } from "./PlanTrialOptInModal/ErrorState";
import { LeftPane } from "./PlanTrialOptInModal/LeftPane";
import { LoadingState } from "./PlanTrialOptInModal/LoadingState";
import { RightPane } from "./PlanTrialOptInModal/RightPane";
import { SuccessState } from "./PlanTrialOptInModal/SuccessState";
import { I18N_LOCALE_ROOT } from "./PlanTrialOptInModal/constants";
import {
  TRIAL_MODAL_STATES,
  type TrialModalState,
} from "./PlanTrialOptInModal/modalConstants";
import { useCurrentPlanApi } from "./hooks/useCurrentPlanApi";

interface PlanTrialOptInModalProps {
  onCancel?: () => void;
  onConfirm?: () => void;
  onSuccess?: () => void;
  planTier?: string;
  trialType?: string;
}

const PlanTrialOptInModalComponent = ({
  planTier = planTiers.business,
  onConfirm,
  onCancel,
  onSuccess,
  trialType = "business_thirty_day",
}: PlanTrialOptInModalProps) => {
  const modal = useModal();
  const { refetchCurrentPlan, currentPlan } = useCurrentPlanApi();
  const isDowngradeScheduled =
    currentPlan?.is_community_hub_downgrade_scheduled;

  const [modalState, setModalState] = useState<TrialModalState>(
    isDowngradeScheduled
      ? TRIAL_MODAL_STATES.BLOCKED_DOWNGRADE_SCHEDULED
      : TRIAL_MODAL_STATES.DETAILS,
  );
  const [errorMessage, setErrorMessage] = useState<string>();

  const { plan, isPlanLoading } = useFetchPlan({
    planTier,
  });
  const displayPlanName = plan?.display_name;

  const createPlanTrialMutation = useCreatePlanTrial({
    onSuccess: () => {
      void refetchCurrentPlan();
      setModalState(TRIAL_MODAL_STATES.SUCCESS);
      onConfirm?.();
    },
    onError: error => {
      setModalState(TRIAL_MODAL_STATES.ERROR);
      setErrorMessage(error.message || t(`${I18N_LOCALE_ROOT}.error.subtitle`));
    },
  });

  const handleClose = () => {
    onCancel?.();
    void modal.hide();
    modal.remove();
  };

  const handleSuccessClose = () => {
    onSuccess?.();
    void modal.hide();
    modal.remove();
  };

  const handleConfirm = async () => {
    // Additional safeguard to prevent trial creation if downgrade is scheduled
    if (isDowngradeScheduled) {
      setModalState(TRIAL_MODAL_STATES.BLOCKED_DOWNGRADE_SCHEDULED);
      return;
    }

    setModalState(TRIAL_MODAL_STATES.LOADING);
    await createPlanTrialMutation.mutateAsync({
      trial_type: trialType,
    });
  };

  const renderModalContent = () => {
    switch (modalState) {
      case TRIAL_MODAL_STATES.BLOCKED_DOWNGRADE_SCHEDULED:
        return <BlockedDowngradeScheduledState onClose={handleClose} />;
      case TRIAL_MODAL_STATES.LOADING:
        return <LoadingState />;
      case TRIAL_MODAL_STATES.SUCCESS:
        return (
          <SuccessState
            planName={displayPlanName}
            onClose={handleSuccessClose}
            currentPlan="Professional"
          />
        );
      case TRIAL_MODAL_STATES.ERROR:
        return <ErrorState errorMessage={errorMessage} onClose={handleClose} />;
      case TRIAL_MODAL_STATES.DETAILS:
      default:
        return (
          <Modal.Body className="p-0 py-6">
            <div className="flex h-full">
              <div className="w-full pr-4 lg:w-1/2">
                <LeftPane
                  onConfirm={handleConfirm}
                  plan={plan}
                  isPlanLoading={isPlanLoading}
                />
              </div>
              <div className="hidden w-1/2 pl-4 lg:block">
                <RightPane plan={plan} isPlanLoading={isPlanLoading} />
              </div>
            </div>
          </Modal.Body>
        );
    }
  };

  const getModalTitle = () => {
    switch (modalState) {
      case TRIAL_MODAL_STATES.BLOCKED_DOWNGRADE_SCHEDULED:
        return t(`${I18N_LOCALE_ROOT}.blocked_downgrade_scheduled.title`);
      case TRIAL_MODAL_STATES.SUCCESS:
        return t(`${I18N_LOCALE_ROOT}.success.modal_title`);
      case TRIAL_MODAL_STATES.ERROR:
        return t(`${I18N_LOCALE_ROOT}.error.modal_title`);
      case TRIAL_MODAL_STATES.DETAILS:
      case TRIAL_MODAL_STATES.LOADING:
      default:
        return t(`${I18N_LOCALE_ROOT}.title`);
    }
  };

  return (
    <Modal isOpen={modal.visible} onClose={handleClose}>
      <Modal.Overlay />
      <Modal.Content
        size={modalState === TRIAL_MODAL_STATES.DETAILS ? "2xl" : "md"}
      >
        {modalState !== TRIAL_MODAL_STATES.SUCCESS &&
          modalState !== TRIAL_MODAL_STATES.ERROR &&
          modalState !== TRIAL_MODAL_STATES.LOADING &&
          modalState !== TRIAL_MODAL_STATES.BLOCKED_DOWNGRADE_SCHEDULED && (
            <Modal.Header>
              <Modal.Title>{getModalTitle()}</Modal.Title>
              <Modal.HeaderActions>
                <Modal.CloseButton onClick={handleClose} />
              </Modal.HeaderActions>
            </Modal.Header>
          )}
        {renderModalContent()}
      </Modal.Content>
    </Modal>
  );
};

const PlanTrialOptInModalDrawer = ModalManager.create(
  ({
    planTier,
    onConfirm,
    onCancel,
    onSuccess,
    trialType,
  }: PlanTrialOptInModalProps) => (
    <PlanTrialOptInModalComponent
      planTier={planTier}
      onConfirm={onConfirm}
      onCancel={onCancel}
      onSuccess={onSuccess}
      trialType={trialType}
    />
  ),
);

export const usePlanTrialOptInModal = () => useModal(PlanTrialOptInModalDrawer);
